/**
 * Validation for decrypted payloads and imported files.
 *
 * Ciphertext that authenticates is not the same as data we should trust — the
 * user's own older build could have written a shape this build cannot handle.
 * Everything that comes out of `open()` is parsed before the UI sees it.
 */

import { z } from 'zod';
import { PAYLOAD_VERSION } from './types.js';

const shortText = z.string().max(200);
const longText = z.string().max(2000);

export const sequenceStepSchema = z.object({
  id: z.string().min(1).max(64),
  prompt: shortText,
  answerOptions: z.array(shortText).min(2).max(6),
  correctIndex: z.number().int().min(0).max(5),
});

export const anchorPayloadSchema = z
  .object({
    version: z.literal(PAYLOAD_VERSION),
    displayName: z.string().min(1).max(80),
    storageMedium: shortText,
    hintLevels: z.tuple([shortText, shortText, shortText, shortText, shortText]),
    questionSequence: z.array(sequenceStepSchema).min(1).max(8),
    exactLocationHint: longText,
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .refine(
    (p) => p.questionSequence.every((s) => s.correctIndex < s.answerOptions.length),
    { message: 'correctIndex out of range for its answerOptions' },
  );

export const sealedSchema = z.object({
  iv: z.string().min(1).max(64),
  ct: z.string().min(1).max(200_000),
});

export const wrappedKeySchema = z.object({
  kind: z.enum(['passphrase', 'biometric', 'session']),
  iv: z.string().min(1).max(64),
  ct: z.string().min(1).max(4096),
  createdAt: z.number(),
});

export const kdfSchema = z.object({
  algorithm: z.literal('argon2id'),
  memoryKiB: z.number().int(),
  iterations: z.number().int(),
  parallelism: z.number().int(),
  keyLengthBytes: z.number().int(),
});

/**
 * The export envelope, exactly as specified:
 *   { v, kdf, salt, wrappedMK_P, anchors[{ id, iv, ct }] }
 *
 * It carries no plaintext at all — not even a display name — so a leaked file
 * reveals only that someone uses this app and how many anchors they have.
 */
export const exportFileSchema = z.object({
  v: z.literal(1),
  kdf: kdfSchema,
  salt: z.string().regex(/^[0-9a-f]{32}$/),
  wrappedMK_P: wrappedKeySchema,
  anchors: z
    .array(
      z.object({
        id: z.string().min(1).max(120),
        iv: z.string().min(1).max(64),
        ct: z.string().min(1).max(200_000),
      }),
    )
    .max(200),
  exportedAt: z.number(),
});

export type ExportFile = z.infer<typeof exportFileSchema>;
